import React, { useEffect, useState } from "react";
import axios from "axios";
import ReactPaginate from "react-paginate";
import Table from "../../components/Table/Table";
import classes from "./Cars.module.css";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6";
import CommonButton from "../../components/ui/CommonButton/CommonButton";
import { useNavigate } from "react-router-dom";
import InputField from "../../components/ui/InputField/InputField";
import { env } from "../../constants/env";
import { toast } from "react-toastify";

const ITEMS_PER_PAGE = 5;

const Cars = () => {
  const [cars, setCars] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(0); // zero-based for react-paginate
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCar, setSelectedCar] = useState(null);
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [carToDelete, setCarToDelete] = useState(null);

  const navigate = useNavigate();

  // Columns for table
  const columns = [
    // { key: "id", label: "ID" },
    { key: "car", label: "CAR" },
    { key: "type", label: "TYPE" },
    { key: "localPrice", label: "LOCAL/DAY" },
    { key: "outsidePrice", label: "OUTSIDE/DAY" },
    { key: "status", label: "STATUS" },
    { key: "bookings", label: "BOOKINGS" },
  ];

  // Fetch the current page of cars from the backend whenever the page or search term changes
  useEffect(() => {
    const fetchCars = async () => {
      try {
        const res = await axios.get(`${env.apiUrl}/api/cars`, {
          params: {
            page: currentPage + 1,
            limit: ITEMS_PER_PAGE,
            search: searchTerm || undefined,
          },
        });
        setCars(res.data.cars);
        setTotal(res.data.total);
      } catch (err) {
        console.error("Failed to fetch cars", err);
      }
    };
    fetchCars();
  }, [currentPage, searchTerm]);

  const pageCount = Math.ceil(total / ITEMS_PER_PAGE);

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0);
  };

  const handleDeleteCar = (carId) => {
    setCarToDelete(carId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteCar = async () => {
    setShowDeleteConfirm(false);
    try {
      await axios.delete(`${env.apiUrl}/api/cars/${carToDelete}`);
      setCars((prev) => prev.filter((car) => car.id !== carToDelete));
      setTotal((prev) => prev - 1);
      toast.success("Car deleted successfully");
      setShowTableDialog(false);
    } catch (err) {
      console.error("Failed to delete car", err);
      toast.error("Failed to delete car");
    } finally {
      setCarToDelete(null);
    }
  };

  return (
    <div className={classes.wrapper}>
      {showDeleteConfirm && (
        <div className={classes.modalOverlay} onClick={() => setShowDeleteConfirm(false)}>
          <div className={classes.confirmModal} onClick={(e) => e.stopPropagation()}>
            <h3 className={classes.confirmTitle}>Delete car</h3>
            <p className={classes.confirmText}>Are you sure you want to delete this car? This action cannot be undone.</p>
            <div className={`d-flex gap-3 ${classes.confirmActions}`}>
              <button className={classes.cancelBtn} onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button className={classes.deleteBtn} onClick={confirmDeleteCar}>Delete</button>
            </div>
          </div>
        </div>
      )}
      <div className={`d-flex justify-content-between align-items-center mb-3 ${classes.headerRow}`}>
        <InputField
          search
          placeholder="Search cars or owner name"
          inputContainerClass={classes.inputContainer}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CommonButton title="Add new car" onClick={() => navigate("add-car")} />
      </div>

      <Table
        dataType={"Cars"}
        data={cars}
        columns={columns}
        showDialog={showTableDialog}
        setShowDialog={setShowTableDialog}
        clickedIndex={setSelectedCar}
        carInfo={() => navigate(`car-details/${cars[selectedCar]?.id}`)}
        editCar={() => navigate(`edit-car/${cars[selectedCar]?.id}`)}
        deleteCar={() => handleDeleteCar(cars[selectedCar]?.id)}
      />

      {/* Pagination */}
      <div className="mt-4">
        <ReactPaginate
          previousLabel={
            <FaChevronLeft
              color={
                currentPage === 0
                  ? "var(--border-default)"
                  : "var(--text-secondary-color)"
              }
            />
          }
          nextLabel={
            <FaChevronRight
              color={
                currentPage === pageCount - 1
                  ? "var(--border-default)"
                  : "var(--text-secondary-color)"
              }
            />
          }
          breakLabel={"..."}
          pageCount={pageCount}
          marginPagesDisplayed={2}
          pageRangeDisplayed={1}
          onPageChange={handlePageClick}
          containerClassName={"pagination"}
          subContainerClassName={"pages pagination"}
          previousLinkClassName={`page-link ${
            currentPage === 0 ? "disabled" : ""
          }`}
          nextLinkClassName={`page-link ${
            currentPage === pageCount - 1 ? "disabled" : ""
          }`}
          activeClassName={"active"}
        />
      </div>
    </div>
  );
};

export default Cars;
